// src/components/AddPaciente.jsx

import { useState } from 'react';
import axiosInstance from '../axiosInstance';
import { toast } from 'react-toastify';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Avatar,
  IconButton,
  Typography,
  Paper,
  Divider,
  useTheme
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import PersonIcon from '@mui/icons-material/Person';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';

const AddPaciente = ({ onListChange, onClose }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const fromAddCita = location.state?.fromAddCita || false;
  const returnToCitas = location.state?.returnToCitas || false;

  const [nombres, setNombres] = useState('');
  const [apellidos, setApellidos] = useState('');
  const [fechaNacimiento, setFechaNacimiento] = useState('');
  const [sexo, setSexo] = useState('Masculino');
  const [dni, setDni] = useState('');
  const [residencia, setResidencia] = useState('');
  const [grupoSanguineo, setGrupoSanguineo] = useState('');
  const [email, setEmail] = useState('');
  const [telefono, setTelefono] = useState('');
  const [profesion, setProfesion] = useState('');
  const [observaciones, setObservaciones] = useState(''); // Nuevo estado para observaciones
  const [fotoPerfil, setFotoPerfil] = useState(null); // Archivo
  const [previewImage, setPreviewImage] = useState(null); // Vista previa

  // Lista de grupos sanguíneos
  const gruposSanguineos = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];

  const compressImage = (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const maxWidth = 600;
          const maxHeight = 600;
          let width = img.width;
          let height = img.height;

          if (width > height) {
            if (width > maxWidth) {
              height = Math.round((height * maxWidth) / width);
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width = Math.round((width * maxHeight) / height);
              height = maxHeight;
            }
          }

          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          canvas.toBlob(
            (blob) => {
              const compressedFile = new File([blob], file.name, {
                type: 'image/jpeg',
                lastModified: Date.now(),
              });
              resolve(compressedFile);
            },
            'image/jpeg',
            0.5
          );
        };
      };
    });
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        toast.info('La imagen es grande, se comprimirá automáticamente');
      }
      const compressedFile = await compressImage(file);
      // Compresión adicional si aún pesa >1MB
      const finalFile = compressedFile.size > 1 * 1024 * 1024 ? await compressImage(compressedFile) : compressedFile;

      const previewUrl = URL.createObjectURL(finalFile);
      setPreviewImage(previewUrl);
      setFotoPerfil(finalFile);
      toast.success('Imagen procesada correctamente');
    } catch (error) {
      console.error('Error al comprimir la imagen:', error);
      toast.error('Error al procesar la imagen');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validaciones mínimas
    if (!nombres.trim() || !apellidos.trim() || !fechaNacimiento || !dni.trim()) {
      toast.error('Completa los campos obligatorios');
      return;
    }

    // Armar FormData
    const formData = new FormData();
    formData.append('nombres', nombres);
    formData.append('apellidos', apellidos);
    formData.append('fecha_nacimiento', fechaNacimiento);
    formData.append('sexo', sexo);
    formData.append('dni', dni);
    if (residencia) formData.append('residencia', residencia);
    if (grupoSanguineo) formData.append('grupo_sanguineo', grupoSanguineo);
    if (email) formData.append('email', email);
    if (telefono) formData.append('telefono_principal', telefono);
    if (profesion) formData.append('profesion', profesion);
    if (observaciones) formData.append('observaciones', observaciones);
    if (fotoPerfil) formData.append('foto_perfil', fotoPerfil);

    try {
      // Usa axiosInstance; elimina Content-Type para que axios ponga boundary automático
      const response = await axiosInstance.post('/pacientes/', formData, {
        headers: {
          'Content-Type': undefined
          // Nota: axiosInstance añadirá Authorization automáticamente para rutas de médico
        }
      });

      const nuevoPacienteId = response.data.id_paciente;
      toast.success('¡Paciente creado con éxito!');

      if (fromAddCita || returnToCitas) {
        onClose?.();
        navigate('/doctor/citas', {
          state: {
            fromAddPaciente: true,
            pacienteCreadoId: nuevoPacienteId,
            openAddCitaModal: true
          }
        });
      } else {
        onListChange?.(nuevoPacienteId);
        // Reset campos
        setNombres('');
        setApellidos('');
        setFechaNacimiento('');
        setSexo('Masculino');
        setDni('');
        setResidencia('');
        setGrupoSanguineo('');
        setEmail('');
        setTelefono('');
        setProfesion('');
        setObservaciones('');
        setFotoPerfil(null);
        setPreviewImage(null);
      }
    } catch (error) {
      const status = error?.response?.status;
      if (status === 413) {
        toast.error('La imagen es demasiado grande. Intenta con una foto más liviana.');
      } else if (status === 429) {
        // DRF suele enviar detail con segundos
        const detail = error?.response?.data?.detail || '';
        const hdr = error?.response?.headers?.['retry-after'];
        const secs = hdr ? parseInt(hdr, 10) : (String(detail).match(/(\d+)\s*seg/)?.[1] || null);
        toast.error(
          secs ? `Has alcanzado el límite de solicitudes. Intenta nuevamente en ~${secs}s.` : 'Has alcanzado el límite de solicitudes. Intenta nuevamente en un momento.'
        );
      } else if (error?.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else if (status === 400 || status === 422) {
        // Muestra errores de validación si vienen en un dict
        const data = error.response.data;
        const firstKey = Object.keys(data)[0];
        const firstMsg = Array.isArray(data[firstKey]) ? data[firstKey][0] : data[firstKey];
        toast.error(String(firstMsg || 'Revisa los campos del formulario.'));
      } else {
        toast.error('Hubo un error al crear el paciente. Revisa los campos.');
      }
      console.error('Error al crear el paciente:', error?.response?.data || error?.message);
    }
  };

  // Estilos comunes para los fields
  const textFieldStyle = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '12px',
      transition: 'all 0.2s ease',
      '&:hover': {
        boxShadow: `0 0 0 1px ${alpha(theme.palette.primary.main, 0.2)}`
      },
      '&.Mui-focused': {
        boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.3)}`
      }
    },
    '& .MuiInputLabel-root': {
      fontSize: '0.95rem'
    },
    mb: 1.5
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 3 },
        borderRadius: '16px',
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        backgroundColor: alpha(theme.palette.background.paper, 0.8),
        backdropFilter: 'blur(10px)',
        overflow: 'hidden',
      }}
    >
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}
      >
        {/* Foto de perfil */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2, position: 'relative' }}>
          <Box
            sx={{
              position: 'relative',
              mb: 1,
              '&::after': {
                content: '""',
                position: 'absolute',
                top: -4,
                left: -4,
                right: -4,
                bottom: -4,
                borderRadius: '50%',
                background: `linear-gradient(45deg, ${alpha(theme.palette.primary.main, 0.2)}, ${alpha(theme.palette.secondary.main, 0.2)})`,
                zIndex: -1,
                opacity: 0.7
              }
            }}
          >
            <Avatar
              src={previewImage}
              sx={{
                width: 130,
                height: 130,
                bgcolor: sexo === 'Masculino' ? alpha(theme.palette.primary.main, 0.9) : alpha(theme.palette.secondary.main, 0.9),
                fontSize: '3rem',
                fontWeight: 'bold',
                boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
                border: `4px solid ${theme.palette.background.paper}`,
                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                '&:hover': {
                  transform: 'scale(1.02)',
                  boxShadow: '0 10px 20px rgba(0,0,0,0.15)',
                }
              }}
            >
              {nombres && apellidos ? `${nombres.charAt(0)}${apellidos.charAt(0)}` : <PersonIcon sx={{ fontSize: '3.5rem' }} />}
            </Avatar>
            <input accept="image/*" type="file" id="foto-perfil-input" onChange={handleFileChange} style={{ display: 'none' }} />
            <label htmlFor="foto-perfil-input">
              <IconButton
                component="span"
                sx={{
                  position: 'absolute',
                  bottom: 5,
                  right: 5,
                  bgcolor: theme.palette.primary.main,
                  color: 'white',
                  width: 40,
                  height: 40,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                  transition: 'all 0.2s ease',
                  '&:hover': { bgcolor: theme.palette.primary.dark, transform: 'scale(1.1)' }
                }}
              >
                <PhotoCameraIcon fontSize="small" />
              </IconButton>
            </label>
          </Box>

          <Typography variant="subtitle2" color="text.secondary" align="center" sx={{ fontWeight: 500, opacity: 0.8 }}>
            Añadir foto (opcional)
          </Typography>
        </Box>

        <Divider
          sx={{
            my: 2,
            opacity: 0.6,
            '&::before, &::after': {
              borderColor: alpha(theme.palette.divider, 0.5),
            }
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ px: 1, fontWeight: 500 }}>
            Datos personales
          </Typography>
        </Divider>

        {/* Form fields... resto del UI igual al original */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          <TextField label="Nombres" value={nombres} onChange={e => setNombres(e.target.value)} required fullWidth sx={{ ...textFieldStyle, flex: '1 1 45%' }} />
          <TextField label="Apellidos" value={apellidos} onChange={e => setApellidos(e.target.value)} required fullWidth sx={{ ...textFieldStyle, flex: '1 1 45%' }} />
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          <TextField type="date" label="Fecha de Nacimiento" InputLabelProps={{ shrink: true }} value={fechaNacimiento} onChange={e => setFechaNacimiento(e.target.value)} required sx={{ ...textFieldStyle, flex: '1 1 45%' }} />
          <FormControl sx={{ ...textFieldStyle, flex: '1 1 45%' }}>
            <InputLabel>Sexo</InputLabel>
            <Select value={sexo} label="Sexo" onChange={e => setSexo(e.target.value)} MenuProps={{ PaperProps: { sx: { borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' } } }}>
              <MenuItem value="Masculino">Masculino</MenuItem>
              <MenuItem value="Femenino">Femenino</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <TextField label="DNI" value={dni} onChange={e => setDni(e.target.value)} required fullWidth sx={textFieldStyle} />
        <TextField label="Lugar de Residencia (Opcional)" value={residencia} onChange={e => setResidencia(e.target.value)} fullWidth sx={textFieldStyle} />

        <Divider
          sx={{
            my: 2,
            opacity: 0.6,
            '&::before, &::after': {
              borderColor: alpha(theme.palette.divider, 0.5),
            }
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ px: 1, fontWeight: 500 }}>
            Información adicional
          </Typography>
        </Divider>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          <FormControl sx={{ ...textFieldStyle, flex: '1 1 45%' }}>
            <InputLabel>Grupo Sanguíneo (Opcional)</InputLabel>
            <Select value={grupoSanguineo} label="Grupo Sanguíneo (Opcional)" onChange={e => setGrupoSanguineo(e.target.value)} MenuProps={{ PaperProps: { sx: { borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' } } }}>
              <MenuItem value=""><em>No especificado</em></MenuItem>
              {gruposSanguineos.map((tipo) => (
                <MenuItem key={tipo} value={tipo}>{tipo}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField label="Profesión (Opcional)" value={profesion} onChange={e => setProfesion(e.target.value)} sx={{ ...textFieldStyle, flex: '1 1 45%' }} />
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          <TextField label="Email (Opcional)" type="email" value={email} onChange={e => setEmail(e.target.value)} sx={{ ...textFieldStyle, flex: '1 1 45%' }} />
          <TextField label="Teléfono (Opcional)" value={telefono} onChange={e => setTelefono(e.target.value)} sx={{ ...textFieldStyle, flex: '1 1 45%' }} />
        </Box>

        <TextField
          label="Observaciones (Opcional)"
          value={observaciones}
          onChange={e => setObservaciones(e.target.value)}
          multiline
          rows={4}
          fullWidth
          sx={{
            ...textFieldStyle,
            mt: 1,
            '& .MuiOutlinedInput-root': {
              borderRadius: '12px',
              backgroundColor: alpha(theme.palette.background.paper, 0.4),
            }
          }}
        />

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 2,
            mt: 3,
            position: 'sticky',
            bottom: 0,
            pb: 1,
            pt: 2,
            backgroundColor: alpha(theme.palette.background.paper, 0.7),
            backdropFilter: 'blur(10px)',
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            zIndex: 1
          }}
        >
          <Button
            onClick={onClose}
            color="inherit"
            variant="outlined"
            startIcon={<CancelIcon />}
            sx={{
              borderRadius: '10px',
              px: 2,
              py: 1,
              borderColor: alpha(theme.palette.divider, 0.5),
              color: theme.palette.text.secondary,
              '&:hover': {
                backgroundColor: alpha(theme.palette.divider, 0.1),
                borderColor: alpha(theme.palette.divider, 0.8),
              }
            }}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            startIcon={<SaveIcon />}
            sx={{
              borderRadius: '10px',
              px: 3,
              py: 1,
              boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
              transition: 'all 0.3s ease',
              background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${alpha(theme.palette.primary.dark, 0.9)})`,
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 15px rgba(0,0,0,0.15)',
                background: `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
              }
            }}
          >
            Crear Paciente
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

export default AddPaciente;
